import { Injectable } from '@angular/core';
import { Device } from '@capacitor/device';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import { AuthenticatedHttpService } from '../authenticatedHttpService.service';
import { CommonPushNotification, NotificationType } from 'src/shared/models/push-notification/push-notification';
import { createNotificationChannels } from './push-notification.channels';
import { LocalStorageService } from '../local-storage.service';
import { environment } from 'src/environments/environment';
import { Subject } from 'rxjs';
import { HttpUtils } from 'src/shared/utils/http-utils';
import { StorageProvider } from '../storage.provider';
import { App } from '@capacitor/app';

export interface IUnreadPushNotification {
    type: NotificationType,
    count: number,
}

@Injectable({ providedIn: "root" })
export class PushProvider {
    static readonly PUSH_NOTIFICATIONS = "PushProvider_PushNotifications";

    static readonly FIREBASE_ID: string = "FIREBASE_ID";
    static readonly LINK_REGISTER: string = "deviceregistrations";
    static readonly LINK_UNREGISTER: string = "deviceregistrations/firebasedeviceid/";

    private readonly _onPushNotification = new Subject<CommonPushNotification>();
    public onPushNotification$ = this._onPushNotification.asObservable();

    deviceRegistered: boolean;
    deviceRegistration: boolean;
    denyPermission: boolean;

    constructor(
        private http: AuthenticatedHttpService,
        private storage: LocalStorageService,
        private storageProvider: StorageProvider,
    ) { }

    async setupPush() {
        try {
            // Request permissions for Firebase Messaging
            const { receive } = await FirebaseMessaging.requestPermissions();
            if (receive === 'granted') {
                console.log('Firebase messaging permission granted');

                // Get FCM token
                try {
                    const { token } = await FirebaseMessaging.getToken();
                    if (token) {
                        console.log('FCM token received:', token);
                        await this.registerDevice(token);
                    }
                } catch (error) {
                    console.error('Error getting FCM token:', error);
                }
            } else {
                console.log('Firebase messaging permission denied');
                this.denyPermission = true;
            }

            // Listen for token refresh
            FirebaseMessaging.addListener('tokenReceived', async (event) => {
                console.log('FCM token refreshed:', event.token);
                await this.registerDevice(event.token);
            });
        }
        catch (e) {
            console.log(e)
        }
    }

    async purge() {
        if (!this.deviceRegistered) return;

        this.deviceRegistered = false;
        const firebaseId = await this.storage.get(PushProvider.FIREBASE_ID);
        try {
            await this.http.delete(environment.web.apiUrl + PushProvider.LINK_UNREGISTER + encodeURIComponent(firebaseId))
        } catch (error) {
            console.log("Device error while Unregistering on server", error);
        }

        // Remove Firebase Messaging listeners
        await FirebaseMessaging.removeAllListeners();
    }

    listen() {
        if (this.storageProvider.isAndroid) {
            createNotificationChannels();
        }

        // receiving push-notification event
        this.onReceivedMessage();

        // click on push-notification event
        this.onReceivedMessageClick();
    }

    private async registerDevice(token: string) {
        try {
            if (this.deviceRegistration || this.deviceRegistered) return;
            this.deviceRegistration = true;

            var device = await Device.getInfo();
            var deviceId = await Device.getId();
            const appInfo = await App.getInfo();
            const language = await Device.getLanguageCode();

            const response = await HttpUtils.handle<string>({
                request: this.http.post(environment.web.apiUrl + PushProvider.LINK_REGISTER, JSON.stringify({
                    name: device.model,
                    description: deviceId.identifier,
                    firebaseDeviceId: token,
                    appVersion: appInfo.version,
                    language: language.value,
                })),
                stringifyError: true,
            });

            await this.storage.set(PushProvider.FIREBASE_ID, token);

            this.deviceRegistered = true;
        }
        catch (error) {
            if (error === 'OK') {
                await this.storage.set(PushProvider.FIREBASE_ID, token);
                console.log('device already registered');
                this.deviceRegistered = true;
            }
            else {
                console.log("Device error while registering on server", error);
                this.deviceRegistered = false;
            }
        }
        finally {
            this.deviceRegistration = false;
        }
    }

    private onReceivedMessage() {
        FirebaseMessaging.addListener('notificationReceived', (event) => {
            if (!event.notification || !event.notification.data) return;

            const data = event.notification.data as any;

            const notification = {
                title: event.notification.title || '',
                description: event.notification.body || '',
                clickAction: parseInt(data.click_action?.toString() || '0'),
                associatedPage: parseInt(data.associated_page?.toString() || '0'),
                associatedEntryId: data.associated_entry_id,
                additional: data.additional,
                clickNotification: false,
                createdBy: data.created_by,
                createdAt: data.created_at,
                databaseIdentifier: data.dbi,
                type: parseInt(data.type?.toString() || '0'),
            } as CommonPushNotification;

            this._onPushNotification.next(notification);
        });
    }

    private onReceivedMessageClick() {
        FirebaseMessaging.addListener('notificationActionPerformed', (event) => {
            if (!event.notification || !event.notification.data) return;

            const data = event.notification.data as any;

            const notification = {
                title: event.notification.title || '',
                description: event.notification.body || '',
                clickAction: parseInt(data.click_action?.toString() || '0'),
                associatedPage: parseInt(data.associated_page?.toString() || '0'),
                associatedEntryId: data.associated_entry_id,
                additional: data.additional,
                clickNotification: true,
                createdBy: data.created_by,
                createdAt: data.created_at,
                databaseIdentifier: data.dbi,
                type: parseInt(data.type?.toString() || '0'),
            } as CommonPushNotification;

            this._onPushNotification.next(notification);
        });
    }
}